// Collection "Load More" button logic
// Implemented as solution to Locksmith app filtering out products which caused uneven pagination pages
class LoadMoreButton extends HTMLElement {
  constructor() {
    super();
    this.loading = false;
    this.handleClick = this.handleClick.bind(this);
    this.statusEl = null;
    // Store overflow products between clicks
    this.overflowQueue = [];
  }

  connectedCallback() {
    // When the element is added to the DOM, set up the status element and event listener
    this.statusEl = document.getElementById("load-more-status");
    this.addEventListener("click", this.handleClick);

    // Auto-load more products on page load if not enough are shown to fill first page
    window.addEventListener("DOMContentLoaded", () => {
      const grid = document.querySelector(".collection__items");
      if (!grid) return;
      const productsPerPage = parseInt(
        this.dataset.pageSize ||
          this.dataset.productsPerPage ||
          this.dataset.perPage ||
          this.dataset.limit ||
          this.dataset["total-pages"] ||
          15,
        10,
      );
      const currentCount = grid.querySelectorAll("li").length;
      if (currentCount < productsPerPage && this.dataset.nextUrl) {
        // Only load the additional needed
        const toLoad = productsPerPage - currentCount;
        setTimeout(() => {
          this.handleClick({ detail: { overrideProductsToLoad: toLoad } });
        }, 100);
      }
    });
  }

  async handleClick(e) {
    if (this.loading || this.disabled) return;
    // Get product count per paginated page
    const pageSize = parseInt(
      this.dataset.pageSize ||
        this.dataset.productsPerPage ||
        this.dataset.perPage ||
        this.dataset.limit ||
        this.dataset["total-pages"] ||
        15,
      10,
    );
    // Allow override for initial top-up on page load
    let productsToLoad =
      e && e.detail && e.detail.overrideProductsToLoad
        ? e.detail.overrideProductsToLoad
        : pageSize;
    let nextUrl = this.dataset.nextUrl; // The URL for the next paginated page
    this.loading = true;

    // Accessibility updates
    this.setAttribute("aria-busy", "true");
    this.disabled = true;
    this.announce("Loading more products...");

    // Track how many products were actually appended
    let totalAppended = 0;
    // Track if there's more products available to be loaded
    let morePages = true;
    let lastNextBtn = null;

    // The product grid
    const grid = document.querySelector(".collection__items");
    if (!grid) {
      return;
    }

    // Append any overflow products from previous click
    let appended = 0;
    if (this.overflowQueue.length > 0 && productsToLoad > 0) {
      const stillOverflow = [];
      for (let i = 0; i < this.overflowQueue.length; i++) {
        if (productsToLoad <= 0) {
          stillOverflow.push(this.overflowQueue[i]);
          continue;
        }
        const item = this.overflowQueue[i];
        grid.appendChild(item);
        appended++;
        totalAppended++;
        productsToLoad--;
      }
      // Save any remaining overflow products for next click
      this.overflowQueue = stillOverflow;
    }

    // if not at full paginated amount, fetch from next page
    while (
      productsToLoad > 0 &&
      nextUrl &&
      morePages &&
      this.overflowQueue.length === 0
    ) {
      try {
        const response = await fetch(nextUrl, {
          headers: { "X-Requested-With": "XMLHttpRequest" },
        });
        if (!response.ok) throw new Error("error");
        const html = await response.text();
        const temp = document.createElement("div");
        temp.innerHTML = html;
        const newList = temp.querySelector(".collection__items");
        const staticChildren = newList ? Array.from(newList.children) : [];
        appended = 0;

        const overflow = [];
        for (let i = 0; i < staticChildren.length; i++) {
          if (productsToLoad <= 0) {
            overflow.push(staticChildren[i]);
            continue;
          }
          const item = staticChildren[i];
          // Skip over anything Locksmith'd
          if (
            item.nodeType === Node.COMMENT_NODE ||
            (item.tagName === undefined && item.nodeType !== Node.ELEMENT_NODE)
          ) {
            continue;
          }
          // If its not a valid <li> product item, skip it
          if (item.tagName && item.tagName.toLowerCase() !== "li") {
            continue;
          }
          // Append product to grid
          grid.appendChild(item);
          appended++;
          totalAppended++;
          productsToLoad--;
        }
        // Save overflow for next click
        this.overflowQueue = overflow;

        // Find next button for nextUrl
        const nextBtn = temp.querySelector("load-more-button");
        lastNextBtn = nextBtn;
        if (nextBtn && nextBtn.dataset.nextUrl) {
          // More pages available
          nextUrl = nextBtn.dataset.nextUrl;
          morePages = true;
        } else {
          // No more pages
          nextUrl = null;
          morePages = false;
        }
        if (appended === 0 && overflow.length === 0) {
          //break to avoid infinite loop
          break;
        }
      } catch (err) {
        // Handle fetch or parsing errors
        console.error("Load More Debug: Error fetching page", nextUrl, err);
        this.announce("Failed to load more products.");
        this.disabled = false;
        this.removeAttribute("aria-busy");
        this.loading = false;
        return;
      }
    }

    // Announce result to user for accessibility
    if (totalAppended > 0) {
      // Pluralization logic for loaded message
      let loadedMsg =
        window.loadMoreTranslations?.loaded?.other ||
        `${totalAppended} more products loaded.`;
      if (window.loadMoreTranslations?.loaded) {
        if (totalAppended === 1 && window.loadMoreTranslations.loaded.one) {
          loadedMsg = window.loadMoreTranslations.loaded.one;
        } else if (window.loadMoreTranslations.loaded.other) {
          loadedMsg = window.loadMoreTranslations.loaded.other;
        }
      }
      loadedMsg = loadedMsg.replace(
        /\{\{ ?count ?\}\}|\[\[count\]\]/g,
        totalAppended,
      );
      this.announce(loadedMsg);
    } else {
      this.announce(
        window.loadMoreTranslations?.no_more_products ||
          "No more products to load.",
      );
    }

    // Update button state for next page or disable if done
    if (lastNextBtn && lastNextBtn.dataset.nextUrl) {
      this.dataset.nextUrl = lastNextBtn.dataset.nextUrl;
      this.disabled = false;
      this.removeAttribute("aria-busy");
      this.innerHTML =
        window.loadMoreTranslations?.loading_btn || "Load more products";
    } else {
      this.disabled = true;
      this.setAttribute("aria-disabled", "true");
      this.innerHTML =
        window.loadMoreTranslations?.no_more_products || "No more products";
    }

    this.loading = false;
  }

  // Announces a message to screen readers using the ARIA live region.
  announce(msg) {
    if (this.statusEl) {
      this.statusEl.textContent = "";
      setTimeout(() => {
        this.statusEl.textContent = msg;
      }, 10);
    }
  }
}
customElements.define("load-more-button", LoadMoreButton);
